import React, { useState, useRef } from 'react'
import { colors } from './fieldTheme'
import { photoFingerprint, prepareImage, splitDuplicates, duplicateNotice, fingerprintsOf } from './fieldPhotos'
import { CameraIcon, GalleryIcon, ClearIcon, InfoIcon } from './FieldIcons'
import { BottomSheet, SheetGroup, SheetRow } from './FieldUI'

// Photos d'un inventaire (canvas W9, décisions 6 et 32) : « Prendre une photo » ouvre l'appareil
// (capture="environment"), « Choisir dans la galerie » ouvre la photothèque. Chaque photo a une
// description facultative. Les photos restent sur le téléphone jusqu'à l'enregistrement (lot 4).
// Le champ description prend toute la largeur, sous la photo (retour d'Alexandra du 24/10/2026).

let photoSequence = 0

// preview : miniature de 720 px à afficher (url et thumb) à la place de la photo envoyée, bien plus lourde
export function newPhoto(file, user, fingerprint, preview)
{
	photoSequence++
	const url = URL.createObjectURL(preview || file)
	return {
		id: 'photo-' + Date.now() + '-' + photoSequence,
		url,
		thumb: url,
		file,
		fingerprint: fingerprint || null,
		description: '',
		user: user ? user._id : null,
		time: Math.round(Date.now() / 1000)
	}
}

// Fichiers choisis → { photos, duplicates }. knownOf() renvoie les photos déjà présentes au moment
// où les empreintes sont prêtes (une autre sélection a pu arriver entre-temps).
export function preparePhotos(fileList, user, knownOf)
{
	const files = Array.from(fileList || []).filter(file => /^image\//.test(file.type))
	// une photo après l'autre : empreinte (légère), puis réduction des seules photos nouvelles
	const sequence = (list, task) => list.reduce((chain, item) => chain.then(done => task(item).then(result => done.concat([result]))), Promise.resolve([]))
	return sequence(files, file => photoFingerprint(file).then(fingerprint => ({ file, fingerprint })))
		.then(candidates => {
			const { fresh, duplicates } = splitDuplicates(candidates, fingerprintsOf(knownOf()))
			return sequence(fresh, item => prepareImage(item.file).then(prepared => newPhoto(prepared.file, user, item.fingerprint, prepared.preview)))
				.then(photos => ({ photos, duplicates }))
		})
}

// « Cette photo est déjà ajoutée : elle n'a pas été reprise. »
export function DuplicateNote({ count })
{
	const text = duplicateNotice(count)
	if (!text)
		return null
	return (
		<p role="status" style={{ flex: 'none', margin: 0, display: 'flex', alignItems: 'center', gap: 10, padding: '12px 16px', borderRadius: 14, background: '#FFEDD5', color: '#7C2D12', fontSize: 17, fontWeight: 600, lineHeight: 1.35 }}>
			<InfoIcon size={22} />
			{text}
		</p>
	)
}

// knownPhotos : photos déjà présentes ailleurs au même endroit (photos de l'inventaire), pour les doublons
export default function FieldPhotoPicker({ photos, user, knownPhotos, onChange })
{
	const [sheetOpen, setSheetOpen] = useState(false)
	const [preparing, setPreparing] = useState(false)
	const [duplicates, setDuplicates] = useState(0)
	const cameraRef = useRef(null)
	const galleryRef = useRef(null)
	const photosRef = useRef(photos)
	photosRef.current = photos

	const addFiles = (fileList) => {
		setDuplicates(0)
		setPreparing(true)
		preparePhotos(fileList, user, () => photosRef.current.concat(knownPhotos || []))
			.then(result => {
				setPreparing(false)
				setDuplicates(result.duplicates)
				if (result.photos.length > 0)
					onChange(photosRef.current.concat(result.photos))
			})
	}

	const pick = (inputRef) => {
		setSheetOpen(false)
		if (inputRef.current)
			inputRef.current.click()
	}

	const remove = (photo) => {
		URL.revokeObjectURL(photo.url)
		onChange(photos.filter(item => item.id !== photo.id))
	}

	const describe = (photo, description) => {
		onChange(photos.map(item => (item.id === photo.id ? { ...item, description } : item)))
	}

	return (
		<div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
			{photos.map((photo, index) => (
				<div key={photo.id} style={{ flex: 'none', display: 'flex', flexDirection: 'column', gap: 10, background: colors.background, border: '1px solid ' + colors.separator, borderRadius: 20, padding: 12 }}>
					<div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
						<img src={photo.thumb || photo.url} alt={photo.description || 'Photo ' + (index + 1)} style={{ width: 88, height: 88, objectFit: 'cover', borderRadius: 12, flex: 'none', background: '#E8EBF5' }} />
						<span style={{ flex: 1, minWidth: 0, fontSize: 17, fontWeight: 700, color: colors.text }}>{'Photo ' + (index + 1)}</span>
						<button
							type="button"
							aria-label={'Retirer la photo ' + (index + 1)}
							onClick={() => remove(photo)}
							style={{ backgroundImage: 'none', backgroundColor: '#E8EBF5', width: 48, height: 48, borderRadius: 999, border: 0, padding: 0, display: 'flex', alignItems: 'center', justifyContent: 'center', color: colors.text, flex: 'none', cursor: 'pointer' }}
						>
							<ClearIcon />
						</button>
					</div>
					<div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
						<label htmlFor={photo.id + '-description'} style={{ margin: 0, padding: '0 4px', fontSize: 15, fontWeight: 700, lineHeight: 1.3, color: colors.textSecondary }}>{'Description de la photo ' + (index + 1) + ' (facultative)'}</label>
						<input
							id={photo.id + '-description'}
							type="text"
							className="browser-default"
							value={photo.description}
							placeholder="Ce que montre la photo"
							onChange={(e) => describe(photo, e.target.value)}
							style={{ width: '100%', height: 52, margin: 0, padding: '0 14px', borderRadius: 12, border: '2px solid ' + colors.border, fontSize: 17, fontFamily: 'inherit', color: colors.text, background: colors.surface, boxSizing: 'border-box' }}
						/>
					</div>
				</div>
			))}
			{preparing && <p role="status" style={{ margin: 0, fontSize: 17, fontWeight: 600, color: colors.textSecondary }}>Préparation des photos…</p>}
			<DuplicateNote count={duplicates} />
			<button
				type="button"
				className="field-row"
				onClick={() => setSheetOpen(true)}
				style={{ backgroundImage: 'none', backgroundColor: 'transparent', minHeight: 72, width: '100%', borderRadius: 18, border: '2px dashed #9AA3B8', color: colors.primary, fontSize: 18, fontWeight: 700, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 10, fontFamily: 'inherit', cursor: 'pointer' }}
			>
				<CameraIcon />
				{photos.length > 0 ? 'Ajouter une autre photo' : 'Ajouter une photo'}
			</button>
			<input ref={cameraRef} type="file" accept="image/*" capture="environment" hidden onChange={(e) => {
				addFiles(e.target.files)
				e.target.value = ''
			}} />
			<input ref={galleryRef} type="file" accept="image/*" multiple hidden onChange={(e) => {
				addFiles(e.target.files)
				e.target.value = ''
			}} />
			{sheetOpen && (
				<BottomSheet title="Ajouter une photo" onClose={() => setSheetOpen(false)}>
					<SheetGroup>
						<SheetRow label="Prendre une photo" icon={<CameraIcon color={colors.primary} />} onClick={() => pick(cameraRef)} />
						<SheetRow label="Choisir dans la galerie" icon={<GalleryIcon color={colors.primary} />} onClick={() => pick(galleryRef)} />
					</SheetGroup>
					<SheetGroup>
						<SheetRow label="Annuler" icon={<ClearIcon />} onClick={() => setSheetOpen(false)} />
					</SheetGroup>
				</BottomSheet>
			)}
		</div>
	)
}
